import { CalendarOutlined, CloseOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { colorTokens, thematicColors } from '@revenuemarket/design';
import { CheckCircleIcon, CircleMinus } from '@revenuemarket/ui';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { DatePicker, Form, Input, Typography } from 'antd';
import dayjs from 'dayjs';

import { css } from '~libs/emotion';

import { type AdjustmentTaxInvoice } from './SettlementMappingStore';

interface Props {
  adjustmentTaxInvoice: AdjustmentTaxInvoice;
  setAdjustmentTaxInvoice?: (adjustmentTaxInvoice: AdjustmentTaxInvoice, idx: number) => void;
  deleteAdjustmentTaxInvoice?: (idx: number) => void;
  idx: number;
  mode?: 'VIEW' | 'EDIT';
}

const AdjustmentTaxInvoice: React.FC<Props> = ({
  adjustmentTaxInvoice,
  setAdjustmentTaxInvoice,
  deleteAdjustmentTaxInvoice,
  idx,
  mode,
}) => {
  return (
    <Flex gap={8}>
      <Flex
        justify="center"
        style={{
          height: 'inherit',
          position: 'relative',
          width: 16,
          marginLeft: 8,
        }}
      >
        <div
          style={{
            width: 1,
            height: '100%',
            background: colorTokens.borderDefault,
          }}
        />
        <Flex style={{ height: 28, position: 'absolute', top: 15, left: 0 }} align="center">
          <CircleMinus
            style={{
              background: 'white',
            }}
          />
        </Flex>
      </Flex>
      <Flex
        direction="column"
        gap={8}
        style={{
          width: '100%',
          padding: '15px 16px 13px 0px',
        }}
      >
        <Flex justify="space-between">
          <Flex align="center" gap={4}>
            <CheckCircleIcon fill={colorTokens.textDescription} />
            <Typography.Text style={{ margin: 0, fontSize: 14 }}>조정</Typography.Text>
            <DatePicker
              clearIcon={null}
              style={{
                cursor: mode === 'VIEW' ? 'default' : 'pointer',
              }}
              disabledDate={(current) => {
                // 오늘 이후는 선택 불가
                return current && current > dayjs().endOf('day');
              }}
              disabled={mode === 'VIEW'}
              suffixIcon={
                <CalendarOutlined
                  style={{
                    display: mode === 'VIEW' ? 'none' : 'block',
                    color:
                      mode === 'VIEW' ? colorTokens.textDescription : thematicColors.primary.medium,
                  }}
                />
              }
              onChange={(value) => {
                setAdjustmentTaxInvoice?.(
                  {
                    reportingDate: value?.format('YYYY-MM-DD') ?? '',
                  },
                  idx,
                );
              }}
              bordered={false}
              value={dayjs(adjustmentTaxInvoice?.reportingDate)}
              format={'M월 D일'}
              className={css`
                &&& .ant-picker-input > input {
                  width: 45px;
                  color: ${mode === 'VIEW'
                    ? colorTokens.textDescription
                    : thematicColors.primary.medium};
                  cursor: ${mode === 'VIEW' ? 'default' : 'pointer'};
                }
                &&& .ant-picker-clear {
                  color: ${mode === 'VIEW'
                    ? colorTokens.textDescription
                    : thematicColors.primary.medium};
                }
              `}
            />
          </Flex>
          {mode !== 'VIEW' && (
            <CloseOutlined
              onClick={() => {
                deleteAdjustmentTaxInvoice?.(idx);
              }}
            />
          )}
        </Flex>
        <Flex align="flex-start" gap={4}>
          {mode === 'VIEW' ? (
            <>
              <Typography.Text style={{ flex: 1, fontSize: 14 }}>
                {adjustmentTaxInvoice?.memo}
              </Typography.Text>
              <Typography.Text style={{ flex: 1, fontSize: 14, textAlign: 'right' }}>
                {adjustmentTaxInvoice?.adjustmentAmount?.toLocaleString() + '원'}
              </Typography.Text>
            </>
          ) : (
            <>
              <Form.Item
                style={{
                  margin: 0,
                }}
                validateStatus={!adjustmentTaxInvoice?.memo ? 'error' : ''}
                help={!adjustmentTaxInvoice?.memo ? '메모는 필수값입니다.' : ''}
              >
                <Input
                  style={{
                    flex: 1,
                  }}
                  placeholder="메모는  입력하세요"
                  onChange={(e) => {
                    setAdjustmentTaxInvoice?.(
                      {
                        memo: e.target.value,
                      },
                      idx,
                    );
                  }}
                  value={adjustmentTaxInvoice?.memo}
                />
              </Form.Item>
              <FormattedInput
                style={{
                  width: 200,
                  flex: 1,
                }}
                className={css`
                  & input {
                    text-align: right;
                  }
                `}
                value={adjustmentTaxInvoice?.adjustmentAmount + ''}
                onChange={(value) => {
                  setAdjustmentTaxInvoice?.(
                    {
                      adjustmentAmount: Number(value),
                    },
                    idx,
                  );
                }}
                suffix="원"
                formatter={(value: string) => {
                  if (value === '' || isNaN(parseFloat(value))) return '';
                  return parseFloat(value).toLocaleString();
                }}
                deformatter={(value: string) => {
                  if (value === '' || isNaN(parseFloat(value))) return '';
                  return value.replace(/,/g, '');
                }}
              />
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AdjustmentTaxInvoice;
